import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { indexedFoldersApi, type IndexedFolderConfig, type FolderIndexStatus, type CreateFolderRequest, type UpdateFolderRequest } from '../api/indexedFoldersApi'

const DEFAULT_STATUS: FolderIndexStatus['status'] = 'pending'
const INDEXING_STATUS: FolderIndexStatus['status'] = 'indexing'

const FALLBACK_ERRORS = {
  fetchFolders: 'Kon mappen niet ophalen.',
  createFolder: 'Kon mapconfiguratie niet aanmaken.',
  updateFolder: 'Kon mapconfiguratie niet bijwerken.',
  deleteFolder: 'Kon mapconfiguratie niet verwijderen.',
  toggleFolder: 'Kon mapstatus niet wijzigen.',
  getFolderStatus: 'Kon mapstatus niet ophalen.',
  getAllFolderStatuses: 'Kon indexeerstatus niet verversen.',
  startIndexing: 'Kon indexeren niet starten.',
} as const

let allStatusesIntervalId: ReturnType<typeof setInterval> | null = null
let isPollingAllStatuses = false
const folderStatusTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

function clearAllStatusesInterval(): void {
  if (allStatusesIntervalId) {
    clearInterval(allStatusesIntervalId)
    allStatusesIntervalId = null
  }
  isPollingAllStatuses = false
}

function clearFolderStatusTimeout(folderId: string): void {
  const timeoutId = folderStatusTimeouts.get(folderId)
  if (timeoutId) {
    clearTimeout(timeoutId)
    folderStatusTimeouts.delete(folderId)
  }
}

function clearAllFolderStatusTimeouts(): void {
  for (const timeoutId of folderStatusTimeouts.values()) {
    clearTimeout(timeoutId)
  }
  folderStatusTimeouts.clear()
}

function toErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message
  }
  return fallback
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function sanitizeFolder(raw: unknown): IndexedFolderConfig | null {
  if (!isRecord(raw)) {
    return null
  }

  if (typeof raw.id !== 'string' || typeof raw.name !== 'string' || typeof raw.folderPath !== 'string') {
    return null
  }

  const folder: IndexedFolderConfig = {
    id: raw.id,
    name: raw.name,
    folderPath: raw.folderPath,
    displayOrder: typeof raw.displayOrder === 'number' ? raw.displayOrder : 0,
    isActive: typeof raw.isActive === 'boolean' ? raw.isActive : false,
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : '',
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : '',
  }

  if (typeof raw.description === 'string') {
    folder.description = raw.description
  }

  return folder
}

function sanitizeFolders(raw: unknown): IndexedFolderConfig[] {
  if (!Array.isArray(raw)) {
    return []
  }

  return raw
    .map(sanitizeFolder)
    .filter((folder): folder is IndexedFolderConfig => folder !== null)
}

function sanitizeFolderStatus(raw: unknown, fallbackFolderId: string): FolderIndexStatus {
  if (!isRecord(raw)) {
    return {
      folderId: fallbackFolderId,
      documentCount: 0,
      folderCount: 0,
      status: DEFAULT_STATUS,
    }
  }

  const status = raw.status
  const normalized: FolderIndexStatus = {
    folderId: typeof raw.folderId === 'string' ? raw.folderId : fallbackFolderId,
    documentCount: typeof raw.documentCount === 'number' ? raw.documentCount : 0,
    folderCount: typeof raw.folderCount === 'number' ? raw.folderCount : 0,
    status:
      status === 'indexed' || status === 'pending' || status === 'indexing' || status === 'error'
        ? status
        : DEFAULT_STATUS,
  }

  if (typeof raw.lastIndexed === 'string') {
    normalized.lastIndexed = raw.lastIndexed
  }

  if (typeof raw.errorMessage === 'string') {
    normalized.errorMessage = raw.errorMessage
  }

  return normalized
}

function sanitizeFolderStatuses(raw: unknown): Record<string, FolderIndexStatus> {
  if (!isRecord(raw)) {
    return {}
  }

  const sanitized: Record<string, FolderIndexStatus> = {}
  for (const [folderId, status] of Object.entries(raw)) {
    sanitized[folderId] = sanitizeFolderStatus(status, folderId)
  }
  return sanitized
}

function sanitizeSelectedFolderId(raw: unknown): string | null {
  return typeof raw === 'string' ? raw : null
}

function initialIndexedFoldersState(): IndexedFoldersState {
  return {
    folders: [],
    folderStatuses: {},
    isLoading: false,
    error: null,
    selectedFolderId: null,
    indexingProgress: {},
  }
}

export interface IndexedFoldersState {
  // State
  folders: IndexedFolderConfig[]
  folderStatuses: Record<string, FolderIndexStatus>
  isLoading: boolean
  error: string | null
  selectedFolderId: string | null
  indexingProgress: Record<string, number>
}

interface IndexedFoldersActions {
  // Query actions
  fetchFolders: () => Promise<void>
  getFolderStatus: (folderId: string) => Promise<void>
  pollFolderStatus: (folderId: string, intervalMs?: number) => Promise<void>
  pollAllFolderStatuses: (intervalMs?: number) => Promise<void>
  stopPollingStatuses: () => void

  // Mutation actions
  createFolder: (config: CreateFolderRequest) => Promise<void>
  updateFolder: (id: string, config: UpdateFolderRequest) => Promise<void>
  deleteFolder: (id: string) => Promise<void>
  toggleFolder: (id: string, isActive: boolean) => Promise<void>
  startIndexing: (folderIds?: string[]) => Promise<void>

  // UI state actions
  selectFolderForEdit: (id: string) => void
  clearSelection: () => void
  setError: (msg: string | null) => void
  clearError: () => void
  setIndexingProgress: (folderId: string, progress: number) => void
  clearStore: () => void
}

type IndexedFoldersStore = IndexedFoldersState & IndexedFoldersActions

export const useIndexedFoldersStore = create<IndexedFoldersStore>()(
  persist(
    (set, get) => ({
      ...initialIndexedFoldersState(),

      // Fetch all folders
      fetchFolders: async () => {
        set({ isLoading: true, error: null })
        try {
          const folders = sanitizeFolders(await indexedFoldersApi.fetchFolders())
          set({ folders, isLoading: false, selectedFolderId: get().selectedFolderId })
        } catch (error) {
          const errorMsg = toErrorMessage(error, FALLBACK_ERRORS.fetchFolders)
          set({ error: errorMsg, isLoading: false })
          throw error
        }
      },

      // Create folder
      createFolder: async (config: CreateFolderRequest) => {
        set({ isLoading: true, error: null })
        try {
          const newFolder = sanitizeFolder(await indexedFoldersApi.createFolder(config))
          if (!newFolder) {
            throw new Error(FALLBACK_ERRORS.createFolder)
          }

          set(state => ({
            folders: [...sanitizeFolders(state.folders), newFolder],
            isLoading: false,
          }))
        } catch (error) {
          const errorMsg = toErrorMessage(error, FALLBACK_ERRORS.createFolder)
          set({ error: errorMsg, isLoading: false })
          throw error
        }
      },

      // Update folder
      updateFolder: async (id: string, config: UpdateFolderRequest) => {
        set({ isLoading: true, error: null })
        try {
          const updatedFolder = sanitizeFolder(await indexedFoldersApi.updateFolder(id, config))
          if (!updatedFolder) {
            throw new Error(FALLBACK_ERRORS.updateFolder)
          }

          set(state => ({
            folders: sanitizeFolders(state.folders).map(f => (f.id === id ? updatedFolder : f)),
            isLoading: false,
          }))
        } catch (error) {
          const errorMsg = toErrorMessage(error, FALLBACK_ERRORS.updateFolder)
          set({ error: errorMsg, isLoading: false })
          throw error
        }
      },

      // Delete folder
      deleteFolder: async (id: string) => {
        set({ isLoading: true, error: null })
        try {
          await indexedFoldersApi.deleteFolder(id)
          clearFolderStatusTimeout(id)

          set(state => ({
            folders: sanitizeFolders(state.folders).filter(f => f.id !== id),
            folderStatuses: Object.fromEntries(
              Object.entries(sanitizeFolderStatuses(state.folderStatuses)).filter(([fid]) => fid !== id)
            ),
            indexingProgress: Object.fromEntries(
              Object.entries(state.indexingProgress ?? {}).filter(([fid]) => fid !== id)
            ),
            selectedFolderId: state.selectedFolderId === id ? null : state.selectedFolderId,
            isLoading: false,
          }))
        } catch (error) {
          const errorMsg = toErrorMessage(error, FALLBACK_ERRORS.deleteFolder)
          set({ error: errorMsg, isLoading: false })
          throw error
        }
      },

      // Toggle folder active status
      toggleFolder: async (id: string, isActive: boolean) => {
        set({ isLoading: true, error: null })
        try {
          const updatedFolder = sanitizeFolder(await indexedFoldersApi.toggleFolder(id, isActive))
          if (!updatedFolder) {
            throw new Error(FALLBACK_ERRORS.toggleFolder)
          }

          set(state => ({
            folders: sanitizeFolders(state.folders).map(f => (f.id === id ? updatedFolder : f)),
            isLoading: false,
          }))
        } catch (error) {
          const errorMsg = toErrorMessage(error, FALLBACK_ERRORS.toggleFolder)
          set({ error: errorMsg, isLoading: false })
          throw error
        }
      },

      // Get status of single folder
      getFolderStatus: async (folderId: string) => {
        try {
          const status = sanitizeFolderStatus(await indexedFoldersApi.getFolderStatus(folderId), folderId)
          set(state => ({
            folderStatuses: {
              ...sanitizeFolderStatuses(state.folderStatuses),
              [folderId]: status,
            },
          }))
        } catch (error) {
          const errorMsg = toErrorMessage(error, FALLBACK_ERRORS.getFolderStatus)
          set(state => ({
            error: errorMsg,
            folderStatuses: {
              ...sanitizeFolderStatuses(state.folderStatuses),
              [folderId]: {
                ...sanitizeFolderStatus(state.folderStatuses[folderId], folderId),
                status: 'error',
                errorMessage: errorMsg,
              },
            },
          }))
        }
      },

      // Poll single folder status
      pollFolderStatus: async (folderId: string, intervalMs = 2000) => {
        clearFolderStatusTimeout(folderId)

        const poll = async () => {
          await get().getFolderStatus(folderId)

          const status = get().folderStatuses[folderId]
          if (status?.status !== INDEXING_STATUS) {
            clearFolderStatusTimeout(folderId)
            return
          }

          const timeoutId = setTimeout(poll, intervalMs)
          folderStatusTimeouts.set(folderId, timeoutId)
        }

        await poll()
      },

      // Poll all folder statuses
      pollAllFolderStatuses: async (intervalMs = 2000) => {
        if (allStatusesIntervalId) {
          return
        }

        const refreshStatuses = async () => {
          if (isPollingAllStatuses) {
            return
          }

          isPollingAllStatuses = true
          try {
            const statuses = sanitizeFolderStatuses(await indexedFoldersApi.getAllFolderStatuses())
            set({ folderStatuses: statuses })

            const isAnyIndexing = Object.values(statuses).some(status => status?.status === INDEXING_STATUS)
            if (!isAnyIndexing) {
              get().stopPollingStatuses()
            }
          } catch (error) {
            const errorMsg = toErrorMessage(error, FALLBACK_ERRORS.getAllFolderStatuses)
            set({ error: errorMsg })
            get().stopPollingStatuses()
          } finally {
            isPollingAllStatuses = false
          }
        }

        await refreshStatuses()

        const hasIndexingAfterRefresh = Object.values(get().folderStatuses).some(
          status => status?.status === INDEXING_STATUS
        )

        if (!hasIndexingAfterRefresh) {
          return
        }

        allStatusesIntervalId = setInterval(() => {
          void refreshStatuses()
        }, intervalMs)
      },

      stopPollingStatuses: () => {
        clearAllStatusesInterval()
        clearAllFolderStatusTimeouts()
      },

      // Start indexing
      startIndexing: async (folderIds?: string[]) => {
        set({ isLoading: true, error: null })
        try {
          await indexedFoldersApi.startIndexing(folderIds)
          set({ isLoading: false })

          // Start polling for status updates
          await get().pollAllFolderStatuses()
        } catch (error) {
          const errorMsg = toErrorMessage(error, FALLBACK_ERRORS.startIndexing)
          set({ error: errorMsg, isLoading: false })
          throw error
        }
      },

      // Select folder for edit
      selectFolderForEdit: (id: string) => {
        set({ selectedFolderId: id })
      },

      // Clear selection
      clearSelection: () => {
        set({ selectedFolderId: null })
      },

      // Set error
      setError: (msg: string | null) => {
        set({ error: msg })
      },

      // Clear error
      clearError: () => {
        set({ error: null })
      },

      // Set indexing progress
      setIndexingProgress: (folderId: string, progress: number) => {
        set(state => ({
          indexingProgress: {
            ...state.indexingProgress,
            [folderId]: progress,
          },
        }))
      },

      clearStore: () => {
        clearAllStatusesInterval()
        clearAllFolderStatusTimeouts()
        set(initialIndexedFoldersState())
      },
    }),
    {
      name: 'indexed-folders-store',
      version: 1,
      partialize: state => ({
        folders: sanitizeFolders(state.folders),
        folderStatuses: sanitizeFolderStatuses(state.folderStatuses),
        selectedFolderId: sanitizeSelectedFolderId(state.selectedFolderId),
      }),
      merge: (persistedState, currentState) => {
        if (!isRecord(persistedState)) {
          return currentState
        }

        return {
          ...currentState,
          folders: sanitizeFolders(persistedState.folders),
          folderStatuses: sanitizeFolderStatuses(persistedState.folderStatuses),
          selectedFolderId: sanitizeSelectedFolderId(persistedState.selectedFolderId),
        }
      },
    }
  )
)
